import { useState } from "react";
import {
  Typography,
  TextField,
  MenuItem,
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { findIndex, pullAt, some } from "lodash";
import InteiroInput from "../../../util/ui/inputs/inteiroInput";
import MoedaInput from "../../../util/ui/inputs/moedaInput";
import { IconPlus } from "@tabler/icons-react";
import {
  OperacaoLeitorBMF,
  OperacaoLeitorBovespa,
} from "../../../../models/operacao";
import { Mercado, TipoOperacao } from "../../../../models/enum";
import { Moment } from "moment-timezone";
import TabelaOperacoes from "../../../util/tabelas/tabelaOperacoes";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DATA_PONTO_DE_PARTIDA } from "../../../../stores/constantes";
import { felixTokens } from "../../../../theme";

/** Ticker, data e código são dado, não texto: mono tabular dentro do campo. */
const campoDado = {
  "& input": {
    fontFamily: felixTokens.fontMono,
    fontVariantNumeric: "tabular-nums",
  },
};

type Props = {
  operacoes: (OperacaoLeitorBovespa | OperacaoLeitorBMF)[];
  setOperacoes?: (ops: (OperacaoLeitorBovespa | OperacaoLeitorBMF)[]) => void;
  readOnly: boolean;
  dataPregao?: Moment;
  usuario?: string;
};
export default function Operacoes({
  operacoes,
  setOperacoes,
  readOnly,
  dataPregao,
  usuario,
}: Props) {
  const [mercado, setMercado] = useState<Mercado>(Mercado.A_VISTA);
  const [tipoOperacao, setTipoOperacao] = useState<TipoOperacao>(
    TipoOperacao.COMPRA
  );
  const [codigoAtivo, setCodigoAtivo] = useState("");
  const [codigoObjeto, setCodigoObjeto] = useState("");
  const [prazo, setPrazo] = useState<Moment | undefined>();
  const [quantidade, setQuantidade] = useState<number | undefined>();
  const [precoUnitario, setPrecoUnitario] = useState<number | undefined>();
  const [valorTotal, setValorTotal] = useState<number | undefined>();
  const updatePreco = (
    quantidade: number | undefined,
    precoUnitario: number | undefined
  ) => {
    setQuantidade(quantidade);
    setPrecoUnitario(precoUnitario);
    // Preço 0 é válido (ativo recebido gratuitamente, ex.: bônus de
    // subscrição) — só não calcula com campo vazio.
    if (quantidade !== undefined && precoUnitario !== undefined)
      setValorTotal(quantidade * precoUnitario);
  };
  const [erros, setErros] = useState({
    codigoAtivo: false,
    quantidade: false,
    precoUnitario: false,
    valorTotal: false,
    prazo: false,
    codigoObjeto: false,
  });

  const camposObrigatoriosVazios = () => {
    let camposComErro = {
      codigoAtivo: false,
      quantidade: false,
      precoUnitario: false,
      valorTotal: false,
      prazo: false,
      codigoObjeto: false,
    };
    if (!codigoAtivo) camposComErro = { ...camposComErro, codigoAtivo: true };
    if (!quantidade) camposComErro = { ...camposComErro, quantidade: true };
    // Preço (e portanto total) 0 é permitido — ativo recebido gratuitamente.
    if (precoUnitario === undefined)
      camposComErro = { ...camposComErro, precoUnitario: true };
    if (valorTotal === undefined)
      camposComErro = { ...camposComErro, valorTotal: true };
    if (mercado === Mercado.OPCOES) {
      if (!prazo) camposComErro = { ...camposComErro, prazo: true };
    }
    if (mercado === Mercado.EXERCICIO_OPCOES) {
      if (!codigoObjeto)
        camposComErro = { ...camposComErro, codigoObjeto: true };
    }

    return camposComErro;
  };

  const addOperation = () => {
    const camposComErro = camposObrigatoriosVazios();
    if (!some(camposComErro) && !!dataPregao && !!usuario) {
      const operacao = new OperacaoLeitorBovespa({
        mercado,
        tipoOperacao,
        codigoAtivo: prepararCodigo(codigoAtivo),
        prazo,
        codigoObjeto,
        quantidade: quantidade!,
        precoUnitario: precoUnitario!,
        valorTotal: valorTotal!,
        dataPregao: dataPregao,
        usuario,
        nota: "",
        ordem: 0,
      });
      setMercado(Mercado.A_VISTA);
      setTipoOperacao(TipoOperacao.COMPRA);
      setCodigoAtivo("");
      setCodigoObjeto("");
      setPrazo(undefined);
      setQuantidade(undefined);
      setPrecoUnitario(undefined);
      setValorTotal(undefined);
      setOperacoes!([...operacoes, operacao]);
      handleClose();
    }
    setErros(camposComErro);
  };

  const removeOperation = (
    operacao: OperacaoLeitorBovespa | OperacaoLeitorBMF
  ) => {
    let ops = operacoes;
    pullAt(ops, findIndex(ops, operacao));
    setOperacoes!([...ops]);
  };

  const handleClose = () => {
    setAbrirPopupExclusao(false);
  };
  const handleOpen = () => {
    setAbrirPopupExclusao(true);
  };
  const [abrirPopupExclusao, setAbrirPopupExclusao] = useState(false);

  const prepararCodigo = (codigoAtivo: string) => {
    if (
      (codigoAtivo.length >= 5 &&
        codigoAtivo[codigoAtivo.length - 1] === "F") ||
      (codigoAtivo.length >= 6 && codigoAtivo[codigoAtivo.length - 1] === "E")
    ) {
      codigoAtivo = codigoAtivo.slice(0, -1);
    }
    return codigoAtivo;
  };

  return (
    <Box display={"flex"} flexDirection="column" width="100%">
      {/* Section header do sistema: eyebrow mono tracked sobre hairline. */}
      <Box
        borderBottom={1}
        borderColor="divider"
        paddingBottom={1}
        marginBottom={1.5}
      >
        <Typography variant="eyebrow" color="text.secondary">
          Operações
        </Typography>
      </Box>

      <TabelaOperacoes
        operacoes={operacoes}
        onDelete={!readOnly ? (op) => removeOperation(op) : undefined}
        showDate
      />

      <Box alignSelf={"center"} paddingTop={2}>
        <Fab
          variant="extended"
          color="primary"
          onClick={handleOpen}
          sx={{ gap: 1 }}
          disabled={
            readOnly ||
            !!!dataPregao ||
            !!!usuario ||
            dataPregao
              .clone()
              .startOf("day")
              .isBefore(DATA_PONTO_DE_PARTIDA.clone().add(1, "month"))
          }
        >
          <IconPlus />
          Adicionar
        </Fab>
        <Dialog
          open={abrirPopupExclusao}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Adicionar operação</DialogTitle>
          <DialogContent>
            <Box display={"flex"} flexDirection="column">
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.secondary">
                    Mercado:
                  </Typography>
                </Box>
                <TextField
                  id="standard-mercado"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  sx={{ width: "18ch" }}
                  select
                  value={mercado}
                  onChange={(e) => setMercado(e.target.value as Mercado)}
                >
                  <MenuItem value={Mercado.A_VISTA}>Vista</MenuItem>
                  <MenuItem value={Mercado.OPCOES}>Opções</MenuItem>
                  <MenuItem value={Mercado.EXERCICIO_OPCOES}>
                    Exercício de Opções
                  </MenuItem>
                </TextField>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                paddingTop={1}
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.secondary">
                    C/V:
                  </Typography>
                </Box>
                <TextField
                  id="standard-tipoOperacao"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  variant="outlined"
                  required
                  sx={{ width: "18ch" }}
                  select
                  value={tipoOperacao}
                  onChange={(e) =>
                    setTipoOperacao(e.target.value as TipoOperacao)
                  }
                >
                  <MenuItem key="C" value={TipoOperacao.COMPRA}>
                    C
                  </MenuItem>
                  <MenuItem key="V" value={TipoOperacao.VENDA}>
                    V
                  </MenuItem>
                </TextField>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                paddingTop={1}
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.secondary">
                    Código Ativo:
                  </Typography>
                </Box>
                <TextField
                  id="standard-codigoAtivo"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "18ch", ...campoDado }}
                  variant="outlined"
                  required
                  error={erros.codigoAtivo}
                  value={codigoAtivo}
                  onChange={(e) =>
                    setCodigoAtivo(e.target.value.toUpperCase().trim())
                  }
                  placeholder={
                    mercado === Mercado.A_VISTA ? "BOVA11" : "BOVAV800"
                  }
                />
              </Box>
              {mercado === Mercado.EXERCICIO_OPCOES && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  paddingTop={1}
                >
                  <Box paddingRight={2}>
                    <Typography variant="labelMono" color="text.secondary">
                      Código Objeto:
                    </Typography>
                  </Box>
                  <TextField
                    id="standard-codigoObjeto"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{ width: "18ch", ...campoDado }}
                    variant="outlined"
                    required
                    error={erros.codigoObjeto}
                    value={codigoObjeto}
                    onChange={(e) =>
                      setCodigoObjeto(e.target.value.toUpperCase())
                    }
                    placeholder="BOVA11"
                  />
                </Box>
              )}
              {mercado === Mercado.OPCOES && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  paddingTop={1}
                >
                  <Box paddingRight={2}>
                    <Typography variant="labelMono" color="text.secondary">
                      Prazo:
                    </Typography>
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      sx={{ width: "18ch", ...campoDado }}
                      slotProps={{
                        textField: { size: "small", error: erros.prazo },
                      }}
                      disabled={mercado !== "Opções"}
                      value={prazo ?? null}
                      onChange={(e) => {
                        if (e?.isValid()) setPrazo(e.startOf("day"));
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                paddingTop={1}
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.secondary">
                    Quantidade:
                  </Typography>
                </Box>
                <TextField
                  id="standard-quantidade"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "18ch" }}
                  variant="outlined"
                  required
                  error={erros.quantidade}
                  value={quantidade}
                  onChange={(e) =>
                    //@ts-ignore
                    updatePreco(e.target.floatValue, precoUnitario)
                  }
                  InputProps={{
                    inputComponent: InteiroInput as any,
                  }}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                paddingTop={1}
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.secondary">
                    Preço Unitário:
                  </Typography>
                </Box>
                <TextField
                  id="standard-precoUnitario"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "18ch" }}
                  variant="outlined"
                  required
                  error={erros.precoUnitario}
                  value={precoUnitario}
                  onChange={(e) =>
                    //@ts-ignore
                    updatePreco(quantidade, e.target.floatValue)
                  }
                  InputProps={{
                    inputComponent: MoedaInput as any,
                  }}
                />
              </Box>
              {/* Total da operação: hairline separando e mono peso 500. */}
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                borderTop={1}
                borderColor="divider"
                marginTop={1.5}
                paddingTop={1.5}
              >
                <Box paddingRight={2}>
                  <Typography variant="labelMono" color="text.primary">
                    Valor Total:
                  </Typography>
                </Box>
                <TextField
                  id="standard-valorTotal"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "18ch", "& input": { fontWeight: 500 } }}
                  variant="outlined"
                  disabled
                  value={valorTotal}
                  InputProps={{
                    inputComponent: MoedaInput as any,
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="inherit">
              Cancelar
            </Button>
            <Button onClick={addOperation} variant="contained" color="success">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
